import React, { useState, useEffect } from 'react';
import ExportButtons from './ExportButtons';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement, PointElement, LinearScale, CategoryScale,
  Title, Tooltip, Legend, BarElement, Filler
} from 'chart.js';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, BarElement, Filler);

const tooltipStyle = {
  backgroundColor: 'rgba(18,18,30,0.95)',
  borderColor: 'rgba(255,255,255,0.1)',
  borderWidth: 1,
  padding: 12,
  cornerRadius: 8,
  titleColor: '#fff',
  bodyColor: 'rgba(255,255,255,0.8)',
};

const gridStyle = { color: 'rgba(255,255,255,0.04)' };
const tickStyle = { color: 'rgba(255,255,255,0.4)', font: { size: 11 } };
const noGrid = { display: false };
const noBorder = { display: false };

const SystemHealth = () => {
  const [concurrentUsers, setConcurrentUsers] = useState(12847);

  useEffect(() => {
    const timer = setInterval(() => {
      setConcurrentUsers(prev => prev + Math.floor(Math.random() * 20 - 8));
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const metrics = [
    { label: 'Uptime', value: '99.97%', icon: 'fas fa-check-circle', color: '#10b981' },
    { label: 'Concurrent Users', value: concurrentUsers.toLocaleString(), icon: 'fas fa-users', color: '#f82581', live: true },
    { label: 'Avg API Latency', value: '62ms', icon: 'fas fa-tachometer-alt', color: '#6366f1' },
    { label: 'Active Servers', value: '24', icon: 'fas fa-server', color: '#f59e0b' },
  ];

  return (
    <div className="sh-container">
      <div className="sh-header">
        <div className="sh-header-left">
          <h4>System Health</h4>
          <p className="sh-subtitle">Real-time infrastructure and performance metrics</p>
        </div>
        <div className="sh-header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ExportButtons
            data={[
              { metric: 'Uptime', value: '99.97%', status: 'Healthy' },
              { metric: 'Concurrent Users', value: concurrentUsers.toLocaleString(), status: 'Live' },
              { metric: 'Avg API Latency', value: '62ms', status: 'Healthy' },
              { metric: 'Active Servers', value: '24', status: 'Healthy' },
              { metric: 'API Latency P50', value: '42ms', status: 'OK' },
              { metric: 'API Latency P95', value: '128ms', status: 'OK' },
              { metric: 'API Latency P99', value: '245ms', status: 'Warning' },
              { metric: 'Database CPU', value: '34%', status: 'Healthy' },
              { metric: 'Database Memory', value: '62%', status: 'Healthy' },
              { metric: 'Database Connections', value: '142/500', status: 'Healthy' },
            ]}
            headers={[
              { key: 'metric', label: 'Metric' },
              { key: 'value', label: 'Value' },
              { key: 'status', label: 'Status' },
            ]}
            title="System Health Report"
            filename="system_health"
          />
          <div className="sh-status-badge">
            <span className="sh-status-dot"></span> All Systems Operational
          </div>
        </div>
      </div>

      <div className="sh-metrics-grid">
        {metrics.map((m, i) => (
          <div key={i} className="sh-metric-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="sh-metric-top">
              <div className="sh-metric-icon" style={{ background: m.color + '15', color: m.color }}>
                <i className={m.icon}></i>
              </div>
              {m.live && <span className="sh-metric-live">● Live</span>}
            </div>
            <div className="sh-metric-value">{m.value}</div>
            <div className="sh-metric-label">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="sh-row-2col">
        <UptimeHistoryChart />
        <ConcurrentUsersTimelineChart />
      </div>

      <ApiLatencyChart />

      <div className="sh-row-2col">
        <DatabaseStatusTable />
        <AutoScalingStatusCards />
      </div>

      <ServerResourceUtilizationTable />
    </div>
  );
};

const UptimeHistoryChart = () => {
  const uptimeData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Uptime %',
      data: [99.92, 99.95, 99.98, 99.99, 99.97, 99.99],
      borderColor: '#10b981',
      backgroundColor: 'rgba(16,185,129,0.1)',
      tension: 0.4,
      fill: true,
      pointRadius: 4,
      pointBackgroundColor: '#10b981',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      borderWidth: 2.5,
    }],
  };

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: tickStyle, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: v => v + '%', min: 99.8, max: 100 }, grid: gridStyle, border: noBorder },
    },
  };

  return (
    <div className="sh-chart-card">
      <div className="sh-chart-head">
        <h6>Uptime History</h6>
        <span className="sh-badge-sm">Last 6 months</span>
      </div>
      <div className="sh-chart-body" style={{ height: 260 }}>
        <Line data={uptimeData} options={chartOpts} />
      </div>
    </div>
  );
};

const ConcurrentUsersTimelineChart = () => {
  const concurrentData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [{
      label: 'Concurrent Users',
      data: [2100, 1800, 1200, 980, 850, 1200, 2800, 5200, 8900, 11200, 12800, 13400, 13200, 12800, 11900, 12100, 12900, 13600, 13100, 12200, 10800, 8500, 6200, 4100],
      borderColor: '#f82581',
      backgroundColor: 'rgba(248,37,129,0.08)',
      tension: 0.4,
      fill: true,
      pointRadius: 0,
      pointHoverRadius: 5,
      borderWidth: 2.5,
    }],
  };

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: { ...tickStyle, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 }, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: v => (v / 1000).toFixed(0) + 'K' }, grid: gridStyle, border: noBorder },
    },
  };

  return (
    <div className="sh-chart-card">
      <div className="sh-chart-head">
        <h6>Concurrent Users Timeline</h6>
        <span className="sh-badge-sm">24h view</span>
      </div>
      <div className="sh-chart-body" style={{ height: 260 }}>
        <Line data={concurrentData} options={chartOpts} />
      </div>
    </div>
  );
};

const ApiLatencyChart = () => {
  const latencyData = {
    labels: ['/api/episodes/stream', '/api/auth/token', '/api/payments/purchase', '/api/content/search', '/api/cdn/manifest'],
    datasets: [
      {
        label: 'P50',
        data: [45, 28, 52, 35, 18],
        backgroundColor: 'rgba(16,185,129,0.6)',
        borderRadius: 4,
        borderSkipped: false,
      },
      {
        label: 'P95',
        data: [78, 52, 95, 68, 42],
        backgroundColor: 'rgba(248,37,129,0.6)',
        borderRadius: 4,
        borderSkipped: false,
      },
      {
        label: 'P99',
        data: [120, 85, 150, 110, 72],
        backgroundColor: 'rgba(245,158,11,0.6)',
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  };

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: true, position: 'top' }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: tickStyle, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: v => v + 'ms' }, grid: gridStyle, border: noBorder },
    },
  };

  return (
    <div className="sh-chart-card">
      <div className="sh-chart-head">
        <h6>API Latency by Endpoint</h6>
        <div className="sh-chart-legend-inline">
          <span><span className="sh-dot" style={{ background: 'rgba(16,185,129,0.6)' }}></span> P50</span>
          <span><span className="sh-dot" style={{ background: 'rgba(248,37,129,0.6)' }}></span> P95</span>
          <span><span className="sh-dot" style={{ background: 'rgba(245,158,11,0.6)' }}></span> P99</span>
        </div>
      </div>
      <div className="sh-chart-body" style={{ height: 300 }}>
        <Bar data={latencyData} options={chartOpts} />
      </div>
    </div>
  );
};

const DatabaseStatusTable = () => {
  const dbInstances = [
    { instance: 'MySQL Primary', role: 'Primary', status: 'healthy', connections: '1,247', size: '84.2 GB', lag: 'N/A' },
    { instance: 'MySQL Replica 1', role: 'Replica', status: 'healthy', connections: '892', size: '84.1 GB', lag: '0.2s' },
    { instance: 'MySQL Replica 2', role: 'Replica', status: 'healthy', connections: '756', size: '84.1 GB', lag: '0.3s' },
    { instance: 'Redis Primary', role: 'Primary', status: 'healthy', connections: '2,145', size: '12.4 GB', lag: 'N/A' },
    { instance: 'Redis Replica', role: 'Replica', status: 'healthy', connections: '1,892', size: '12.4 GB', lag: '0.1s' },
  ];

  return (
    <div className="sh-chart-card">
      <div className="sh-chart-head">
        <h6>Database Status</h6>
      </div>
      <div className="sh-table-wrap">
        <table className="sh-status-table">
          <thead>
            <tr>
              <th>Instance</th>
              <th>Role</th>
              <th>Status</th>
              <th>Connections</th>
              <th>Size</th>
              <th>Replication Lag</th>
            </tr>
          </thead>
          <tbody>
            {dbInstances.map((db, i) => (
              <tr key={i} className="sh-table-row">
                <td className="sh-table-instance">{db.instance}</td>
                <td className="sh-table-role">{db.role}</td>
                <td className="sh-table-status">
                  <span className={`sh-status-badge-small ${db.status}`}>
                    <span className="sh-status-dot"></span> {db.status}
                  </span>
                </td>
                <td className="sh-table-connections">{db.connections}</td>
                <td className="sh-table-size">{db.size}</td>
                <td className="sh-table-lag">{db.lag}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AutoScalingStatusCards = () => {
  const scalingStatus = [
    { name: 'App Servers', current: 24, min: 8, max: 128, icon: 'fas fa-cube' },
    { name: 'Worker Nodes', current: 16, min: 4, max: 64, icon: 'fas fa-cogs' },
    { name: 'CDN Edge', current: 42, min: 20, max: 200, icon: 'fas fa-network-wired' },
  ];

  return (
    <div className="sh-scaling-cards">
      <div className="sh-scaling-header">
        <h6>Auto-Scaling Status</h6>
      </div>
      <div className="sh-scaling-grid">
        {scalingStatus.map((s, i) => (
          <div key={i} className="sh-scaling-card" style={{ animationDelay: `${i * 0.08}s` }}>
            <div className="sh-scaling-icon" style={{ color: '#f82581' }}>
              <i className={s.icon}></i>
            </div>
            <div className="sh-scaling-name">{s.name}</div>
            <div className="sh-scaling-current">{s.current}</div>
            <div className="sh-scaling-range">
              <span className="sh-scaling-min">{s.min}</span>
              <div className="sh-scaling-bar">
                <div className="sh-scaling-fill" style={{ width: `${((s.current - s.min) / (s.max - s.min)) * 100}%` }}></div>
              </div>
              <span className="sh-scaling-max">{s.max}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ServerResourceUtilizationTable = () => {
  const servers = [
    { hostname: 'api-server-01', cpu: 68, memory: 72, disk: 45, network: 'Normal', status: 'healthy' },
    { hostname: 'api-server-02', cpu: 72, memory: 68, disk: 42, network: 'Normal', status: 'healthy' },
    { hostname: 'worker-01', cpu: 82, memory: 85, disk: 60, network: 'Elevated', status: 'healthy' },
    { hostname: 'worker-02', cpu: 65, memory: 71, disk: 48, network: 'Normal', status: 'healthy' },
    { hostname: 'cache-01', cpu: 45, memory: 52, disk: 38, network: 'Normal', status: 'healthy' },
    { hostname: 'cdn-edge-01', cpu: 55, memory: 61, disk: 42, network: 'High', status: 'healthy' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'critical': return '#ef4444';
      default: return '#6366f1';
    }
  };

  const getUtilizationColor = (value) => {
    if (value >= 80) return '#ef4444';
    if (value >= 70) return '#f59e0b';
    return '#10b981';
  };

  return (
    <div className="sh-chart-card">
      <div className="sh-chart-head">
        <h6>Server Resource Utilization</h6>
        <span className="sh-badge-sm">6 servers monitored</span>
      </div>
      <div className="sh-table-wrap">
        <table className="sh-resource-table">
          <thead>
            <tr>
              <th>Hostname</th>
              <th>CPU %</th>
              <th>Memory %</th>
              <th>Disk %</th>
              <th>Network</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {servers.map((server, i) => (
              <tr key={i} className="sh-table-row">
                <td className="sh-table-hostname">{server.hostname}</td>
                <td className="sh-table-metric">
                  <div className="sh-metric-bar-wrap">
                    <div className="sh-metric-bar" style={{ width: `${server.cpu}%`, background: getUtilizationColor(server.cpu) }}></div>
                  </div>
                  <span>{server.cpu}%</span>
                </td>
                <td className="sh-table-metric">
                  <div className="sh-metric-bar-wrap">
                    <div className="sh-metric-bar" style={{ width: `${server.memory}%`, background: getUtilizationColor(server.memory) }}></div>
                  </div>
                  <span>{server.memory}%</span>
                </td>
                <td className="sh-table-metric">
                  <div className="sh-metric-bar-wrap">
                    <div className="sh-metric-bar" style={{ width: `${server.disk}%`, background: getUtilizationColor(server.disk) }}></div>
                  </div>
                  <span>{server.disk}%</span>
                </td>
                <td className="sh-table-network">{server.network}</td>
                <td className="sh-table-status">
                  <span className={`sh-status-badge-small ${server.status}`} style={{ color: getStatusColor(server.status) }}>
                    <span className="sh-status-dot" style={{ background: getStatusColor(server.status) }}></span> {server.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SystemHealth;
